import { AuthenticatedRoutes, UnauthenticatedRoutes } from "routes";
import {
  Box,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  chakra,
  useDisclosure,
} from "@chakra-ui/react";
import { FullPageLoading, MaintenancePage } from "pages";
import { setGlobalResetTimer, useSetupAuth0, useToast } from "hooks";

import { Button } from "shared";
import { UserAvatar } from "shared";
import LogRocket from "logrocket";
import { useOpenReplayIdentity } from "shared/openreplay/useOpenReplayIdentity";
import { config } from "config";
import setupLogRocketReact from "logrocket-react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState, useRef, useCallback } from "react";
import { usePermissions } from "contexts";
import { useUserRoles } from "contexts/UserRoles";
import { VStack, HStack } from "@chakra-ui/react";
import {
  ACTION_COMPLETED_KEY,
  COUNTDOWN_SECONDS,
  COUNTDOWN_START_KEY,
  INACTIVITY_LIMIT,
  SESSION_TIMEOUT_KEY,
} from "constants/sessionTimeout";

if (window.location.hostname.includes("galveston")) {
  LogRocket.init("8f4znn/emr");
  setupLogRocketReact(LogRocket);
}

type Settings = {
  allowed: boolean;
  mode: "maintenance" | "normal";
} | null;

type SessionExpiredModalProps = Omit<ModalProps, "children"> & {
  onContinueSession: () => void;
};

function SessionExpiredModal({
  onContinueSession,
  ...props
}: SessionExpiredModalProps) {
  const { logout, user } = useAuth0();
  const { roles } = useUserRoles();
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem(COUNTDOWN_START_KEY);
    localStorage.setItem(SESSION_TIMEOUT_KEY, "true");
    localStorage.removeItem(ACTION_COMPLETED_KEY);
    logout({ returnTo: config.auth0LogoutUrl });
  }, [logout]);

  const calculateRemainingTime = useCallback(() => {
    const startTimestamp = localStorage.getItem(COUNTDOWN_START_KEY);
    if (!startTimestamp) {
      return COUNTDOWN_SECONDS;
    }
    const startTime = parseInt(startTimestamp, 10);
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const remaining = Math.max(0, COUNTDOWN_SECONDS - elapsed);
    return remaining;
  }, []);

  const updateCountdown = useCallback(() => {
    const actionCompleted = localStorage.getItem(ACTION_COMPLETED_KEY);
    const remaining = calculateRemainingTime();
    setCountdown(remaining);
    if (remaining <= 0 && actionCompleted != "pending") {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      handleLogout();
    }
  }, [calculateRemainingTime, handleLogout]);

  useEffect(() => {
    if (props.isOpen) {
      const startTimestamp = Date.now().toString();
      localStorage.setItem(COUNTDOWN_START_KEY, startTimestamp);
      setCountdown(COUNTDOWN_SECONDS);

      updateCountdown();

      countdownIntervalRef.current = setInterval(() => {
        updateCountdown();
      }, 1000);

      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          updateCountdown();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      localStorage.removeItem(COUNTDOWN_START_KEY);
      setCountdown(COUNTDOWN_SECONDS);
    }
  }, [props.isOpen, updateCountdown]);

  const handleContinue = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    localStorage.removeItem(COUNTDOWN_START_KEY);
    setCountdown(COUNTDOWN_SECONDS);
    props.onClose?.();
    onContinueSession();
  };

  const userName = user?.name || "";
  const userRole = roles[0] || "User";

  return (
    <Modal
      isCentered
      {...props}
      onClose={() => {}}
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent userSelect="none">
        <ModalHeader
          display="flex"
          justifyContent="center"
          flexDir="column"
          alignItems="center"
          paddingTop="40px"
          paddingBottom="0"
        >
          <chakra.span
            fontStyle="normal"
            fontWeight="600"
            fontSize="21px"
            lineHeight="25px"
            textAlign="center"
          >
            Session Timeout
          </chakra.span>
        </ModalHeader>
        <ModalBody
          display="flex"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
        >
          <VStack gap="14px" alignItems="center">
            <UserAvatar
              currentUserPicture={true}
              w="80px"
              h="80px"
              marginTop="25px"
            />
            <chakra.span
              fontStyle="normal"
              fontWeight="600"
              fontSize="21px"
              lineHeight="25px"
              textAlign="center"
              marginTop="15px"
            >
              {userName}
            </chakra.span>
            <chakra.span
              fontStyle="normal"
              fontWeight="600"
              fontSize="15px"
              lineHeight="18px"
              color="#9A9EA7"
            >
              {userRole}
            </chakra.span>
            <chakra.span fontSize="17px" textAlign="center" marginTop="25px">
              Your session is about to expire due to inactivity.
            </chakra.span>
            <chakra.span fontSize="17px" textAlign="center" marginTop="15px">
              You will automatically be logged out in:
            </chakra.span>
            <chakra.span
              marginTop="15px"
              fontFamily="'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif"
              fontStyle="normal"
              fontWeight="400"
              fontSize="24px"
              lineHeight="29px"
              textAlign="center"
              color="#9A9EA7"
            >
              {(() => {
                const minutes = Math.floor(countdown / 60);
                const secs = countdown % 60;
                const minStr = minutes < 10 ? `0${minutes}` : String(minutes);
                const secStr = secs < 10 ? `0${secs}` : String(secs);
                return (
                  <>
                    <chakra.span fontSize="48px">{minStr}</chakra.span>{" "}
                    <chakra.span fontSize="17px">min</chakra.span>{" "}
                    <chakra.span fontSize="48px">{secStr}</chakra.span>{" "}
                    <chakra.span fontSize="17px">sec</chakra.span>
                  </>
                );
              })()}
            </chakra.span>
          </VStack>
        </ModalBody>
        <ModalFooter justifyContent="center" gap="10px" paddingTop="30px">
          <HStack spacing="10px">
            <Button
              minW="150px"
              minH="40px"
              onClick={handleContinue}
              colorScheme="blue"
            >
              Continue Session
            </Button>
            <Button
              minW="150px"
              minH="40px"
              onClick={handleLogout}
              variant="outline"
            >
              Log Out
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function UnauthenticatedApp() {
  return (
    <Box height="100vh">
      <UnauthenticatedRoutes />
    </Box>
  );
}

function AuthenticatedApp() {
  const authReady = useSetupAuth0();
  const { isLoadingPermissions, claims } = usePermissions();
  const settings = claims?.settings as Settings;

  if (!authReady || isLoadingPermissions) {
    return <FullPageLoading />;
  }

  return (
    <Box height="100vh">
      <AuthenticatedRoutes />
      {settings?.mode === "maintenance" && !settings?.allowed && (
        <MaintenancePage />
      )}
    </Box>
  );
}

export const App = () => {
  const { isLoading, error, isAuthenticated, user } = useAuth0();
  const toast = useToast();

  const sessionExpiredDisclose = useDisclosure();
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (user && window.location.hostname.includes("galveston.emr-now.com")) {
      LogRocket.identify(user?.sub || "", {
        name: user?.name || "",
        email: user?.email || "",
        picture: user?.picture || "",
        sub: user?.sub || "",
      });
    }
  }, [user]);

  useOpenReplayIdentity();

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    inactivityTimeoutRef.current = setTimeout(() => {
      sessionExpiredDisclose.onOpen();
    }, INACTIVITY_LIMIT);
  }, [sessionExpiredDisclose]);

  useEffect(() => {
    if (!isAuthenticated) {
      const timeoutShown = localStorage.getItem(SESSION_TIMEOUT_KEY);
      if (timeoutShown === "true") {
        localStorage.removeItem(SESSION_TIMEOUT_KEY);
      }
    }
  }, [isAuthenticated]);

  const handleContinueSession = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "touchmove",
      "MSPointerMove",
    ];

    let debounceTimer: NodeJS.Timeout;
    const debouncedResetTimer = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        if (!sessionExpiredDisclose.isOpen) {
          resetInactivityTimer();
        }
      }, 1000);
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, debouncedResetTimer, { passive: true });
    });

    setGlobalResetTimer(resetInactivityTimer);

    resetInactivityTimer();

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, debouncedResetTimer);
      });

      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      setGlobalResetTimer(() => {});
    };
  }, [isAuthenticated, sessionExpiredDisclose, resetInactivityTimer]);

  useEffect(() => {
    if (error) {
      toast({
        description: error?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  if (isLoading) {
    return <FullPageLoading />;
  }

  if (isAuthenticated) {
    return (
      <>
        <AuthenticatedApp />
        <SessionExpiredModal
          {...sessionExpiredDisclose}
          onContinueSession={handleContinueSession}
        />
      </>
    );
  }

  return <UnauthenticatedApp />;
};

export default App;
